import { apiFetch } from './client';

export type PromptLanguage = 'en' | 'he' | 'yi';

export interface SystemPromptRow {
  id: string;
  ref_number: string | null;
  prompt_key: string;
  category: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface SystemPromptTranslationRow {
  id: string;
  prompt_id: string;
  language: PromptLanguage;
  tts_text: string | null;
  audio_s3_key: string | null;
  audio_s3_bucket: string | null;
  audio_mime_type: string | null;
  tone: string;
  is_active_tone: boolean;
  created_at: string;
  updated_at: string;
}

export interface SystemPromptOverrideRow {
  id: string;
  prompt_id: string;
  reseller_id: string | null;
  customer_id: string | null;
  ivr_node_id: string | null;
  language: PromptLanguage;
  tts_text: string | null;
  audio_s3_key: string | null;
  audio_s3_bucket: string | null;
  audio_mime_type: string | null;
  tone: string;
  created_at: string;
  updated_at: string;
}

export interface SystemPromptVersionRow {
  id: string;
  prompt_id: string;
  override_id: string | null;
  language: PromptLanguage;
  tts_text: string | null;
  audio_s3_key: string | null;
  audio_s3_bucket: string | null;
  audio_mime_type: string | null;
  tone: string | null;
  changed_by: string | null;
  changed_at: string;
}

export const LANGUAGE_LABELS: Record<PromptLanguage, string> = {
  en: 'English',
  he: 'Hebrew',
  yi: 'Yiddish',
};

export type SystemPromptNodeOverrideRow = SystemPromptOverrideRow & {
  prompt_key: string;
};

export const systemPrompts = {
  list: () => apiFetch<{ rows: SystemPromptRow[] }>('/api/system-prompts'),

  /**
   * List every override attached to a specific IVR node, keyed by prompt_key.
   * Super-admin only — used by the per-node prompt override panel in the
   * IVR tree editor drawer.
   */
  listForNode: (ivrNodeId: string) =>
    apiFetch<{ rows: SystemPromptNodeOverrideRow[] }>(
      `/api/system-prompts/overrides?ivr_node_id=${encodeURIComponent(ivrNodeId)}`,
    ),

  get: (id: string) =>
    apiFetch<{
      prompt: SystemPromptRow;
      translations: SystemPromptTranslationRow[];
      overrides: SystemPromptOverrideRow[];
    }>(`/api/system-prompts/${id}`),

  upsertTranslation: (
    id: string,
    lang: PromptLanguage,
    body: { tts_text?: string | null; audio_s3_key?: string | null; tone?: string },
  ) =>
    apiFetch<SystemPromptTranslationRow>(`/api/system-prompts/${id}/translations/${lang}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  preview: (id: string, language: PromptLanguage, text?: string) =>
    apiFetch<{ url: string }>(`/api/system-prompts/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify({ language, text }),
    }),

  listOverrides: (id: string) =>
    apiFetch<{ rows: SystemPromptOverrideRow[] }>(`/api/system-prompts/${id}/overrides`),

  upsertOverride: (
    id: string,
    body: {
      language: PromptLanguage;
      tts_text?: string | null;
      audio_s3_key?: string | null;
      tone?: string;
      ivr_node_id?: string | null;
    },
  ) =>
    apiFetch<SystemPromptOverrideRow>(`/api/system-prompts/${id}/overrides`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteOverride: (id: string, overrideId: string) =>
    apiFetch<void>(`/api/system-prompts/${id}/overrides/${overrideId}`, { method: 'DELETE' }),

  /**
   * Upload a custom audio file for a prompt override. The backend creates
   * or updates the override row at the caller's scope (reseller / customer /
   * node via ivr_node_id) and writes the file through the shared storage
   * driver. Returns the updated override row + the uploaded size in bytes.
   */
  uploadOverrideAudio: (
    id: string,
    lang: PromptLanguage,
    body: FormData,
  ) =>
    apiFetch<{ override: SystemPromptOverrideRow; size_bytes: number }>(
      `/api/system-prompts/${id}/overrides/${lang}/audio`,
      { method: 'POST', body },
    ),

  /**
   * Remove the uploaded audio from an override row (keeping any TTS text).
   * If there's also no TTS text left, the whole override row is deleted so
   * resolution falls through to the next level in the chain.
   */
  deleteOverrideAudio: (id: string, overrideId: string) =>
    apiFetch<{ override: SystemPromptOverrideRow | null }>(
      `/api/system-prompts/${id}/overrides/${overrideId}/audio`,
      { method: 'DELETE' },
    ),

  /**
   * Streaming URL for an <audio> tag pointing at the uploaded audio for an
   * override. Mirrors the recordings API — the access-token JWT is appended
   * as a query param because <audio> can't set an Authorization header.
   * The URL goes stale on the next token refresh; callers should re-read
   * it when the auth store's accessToken changes.
   */
  overrideAudioUrl: (id: string, overrideId: string, accessToken: string | null | undefined) => {
    const base = `/api/system-prompts/${id}/overrides/${overrideId}/audio`;
    return accessToken ? `${base}?token=${encodeURIComponent(accessToken)}` : base;
  },

  listVersions: (
    id: string,
    filter?: { language?: PromptLanguage; override_id?: string | null },
  ) => {
    const params = new URLSearchParams();
    if (filter?.language) params.append('language', filter.language);
    if (filter?.override_id === null) params.append('override_id', 'null');
    else if (filter?.override_id) params.append('override_id', filter.override_id);
    const qs = params.toString();
    return apiFetch<{ rows: SystemPromptVersionRow[] }>(
      `/api/system-prompts/${id}/versions${qs ? `?${qs}` : ''}`,
    );
  },

  rollback: (id: string, versionId: string) =>
    apiFetch<{ restored: SystemPromptVersionRow }>(
      `/api/system-prompts/${id}/versions/${versionId}/rollback`,
      { method: 'POST' },
    ),
};
